/**
 * Copyright SimVentions, Inc. Usage, distribution, transferal, and licensing
 * of this source code is protected under SBIR law as described in DFARS 252.227-7018.
 *
 * SBIR data rights fully described in the README.md file in the top level directory of this project.
 */

import quickFormOverviewUrl from "url:../assets/images/quickform_overview.png";
import quickFormUploadUrl from "url:../assets/images/quickform_upload_file.png";
import quickFormDeleteUrl from "url:../assets/images/quickform_delete_file.png";
import quickFormDeleteConfirmationUrl from "url:../assets/images/quickform_delete_file_confirmation.png";
import quickFormTransformationButtonUrl from "url:../assets/images/quickform_transformation.png";
import quickFormDownloadZipUrl from "url:../assets/images/quickform_download_zip.png";
import quickFormDownloadZipConfirmationUrl from "url:../assets/images/quickform_download_zip_confirmation.png";
import quickFormDownloadSingleFileUrl from "url:../assets/images/quickform_download_single_file.png";
import quickFormDownloadSingleFileConfirmationUrl from "url:../assets/images/quickform_download_single_file_confirmation.png";
import quickFormErrorDetailsModalUrl from "url:../assets/images/quickform_error_details_modal.png";
import quickFormErrorDetailsButtonUrl from "url:../assets/images/quickform_table_error_details.png";

export const quickFormContent = `
## Summary

The QuickForm tab performs single or bulk file transformations without the need for manual data mapping that is done in the
Transform Engine. The QuickForm tab can:

- Perform bulk uploads by selecting multiple files from the file explorer, or by uploading .zip or .tar.gz files
- Choose the output file type to transform the uploaded file(s), either individually or in bulk
- Execute transformations either individually or in bulk
- Download the resulting transformation in a .zip file or download individual transformation results

The QuickForm tab currently supports the following transformations:
- Protobuf to XML
- Protobuf to C++ (result is delivered as a .zip archive containing header and source file)
- XML to Protobuf
---

## QuickForm Overview

<img src="${quickFormOverviewUrl}" alt="QuickForm Page" style="max-width: 1100px; height: auto; display: block; margin: 12px auto;" />

**1. File Drop** -
Browse and drop files to upload and display files in QuickForm table

**2. File Info Table** -
Populated from each file uploaded in the file drop 

**3. QuickForm Controls** - 
Perform actions on bulk selected files such as transformation, file type or transform type set, zip download, and deleting rows

**4. File Select** -
Select all, or individual files to perform actions from table controls

**5. Detected Type Dropdown** - 
Dropdown list to update an individual file's detected type 

**6. Transform To Dropdown** -
Dropdown list to update an individual file's transform type

**7. Transform Status** - 
Indicates whether a transformation on a file is pending, successful, or has failed with an error

**8. File Row Actions** -
Download an individual file's transform results, or delete an individual file from the QuickForm upload table 

---

## File Upload and Deletion

### Uploading Files

<img src="${quickFormUploadUrl}" alt="QuickForm Upload" style="max-width: 1100px; height: auto; display: block; margin: 12px auto;" />

**To upload files and view in the QuickForm table**:
1. Click "Browse Files" or drag and drop files from the file system
2. File info rows will be populated for each file selected for upload, or for each file contained in the .zip or .tar.gz file, with an upload status under each file
3. The Detected Type column will be prepopulated with the detected type returned from the ML microservice just like the Transform Engine Upload Modal
4. Transform type will be prepopulated with a supported type
5. A toast will appear with how many files were successfully uploaded

Note: The QuickForm upload feature is subject to the following constraints: 

**Supported File Types:**
- .zip (Containing only Protobuf or XML files)
- .tar.gz (Containing only Protobuf or XML files)
- Protobuf (.proto, .pb2)
- XML (.xml)

**Upload Limits:**
- Maximum upload size: 10MB
- No limit on number of files
- Files can be uploaded individually or in batches

### Deleting Files

<img src="${quickFormDeleteUrl}" alt="QuickForm Delete" style="max-width: 1100px; height: auto; display: block; margin: 12px auto;" />
<img src="${quickFormDeleteConfirmationUrl}" alt="QuickForm Delete Confirmation" style="max-width: 1100px; height: auto; display: block; margin: 12px auto;" />

**To delete files from the QuickForm table** 
1. Click the check box of a single file row to select it, or the check box in the column header to select all rows
2. Click the "Delete Selected" button 
3. File rows will be removed and a toast will appear indicating the number of files removed from the queue

---

### QuickForm File Transformations

<img src="${quickFormTransformationButtonUrl}" alt="QuickForm Transformation" style="max-width: 1100px; height: auto; display: block; margin: 12px auto;" />

For bulk transformations, perform the following in the QuickForm table:
1. Ensure each file has finished uploading before transforming. Files still uploading or in an error state will not be transformed
2. Set the appropriate file type if not currently correct or populated in the file row
3. Set the transform type to a supported transform type if not already set
4. Click the check box in the column header to select all files, or click the check box of individual file rows
5. Click the "Transform Selected" button
6. Transform Status column should update with the success or failure of the transformation 
7. A toast should appear indicating the number of successful transformations

---

### Transform Results Download

There are two ways to download transformation results in the QuickForm tab. You can either download all transform results as a .zip file,
or download individual transformed files through the file row actions


**Download all transformation results**

<img src="${quickFormDownloadZipUrl}" alt="QuickForm Download Zip" style="max-width: 1100px; height: auto; display: block; margin: 12px auto;" />
<img src="${quickFormDownloadZipConfirmationUrl}" alt="QuickForm Download Zip Confirmation" style="max-width: 1100px; height: auto; display: block; margin: 12px auto;" />

1. Click Download All as Zip
2. A toast will appear indicating the .zip file was downloaded successfully 
3. A zip file will be downloaded containing all transformed files

**Download individual file transformation results**

<img src="${quickFormDownloadSingleFileUrl}" alt="QuickForm Download File" style="max-width: 1100px; height: auto; display: block; margin: 12px auto;" />
<img src="${quickFormDownloadSingleFileConfirmationUrl}" alt="QuickForm Download File Confirmation" style="max-width: 1100px; height: auto; display: block; margin: 12px auto;" />

1. In the Actions column of the file row, click the download button
2. A toast will appear indicating the file was downloaded successfully
3. An individual file transformation result will be downloaded  

---

### Troubleshooting Tips

If an error occurs during the transformation process, information on the error can be viewed by clicking  View Error Details  
in the failed file row under the Transform Status

<img src="${quickFormErrorDetailsButtonUrl}" alt="Error Details" style="max-width: 1000px; height: auto; display: block; margin: 12px auto;" />

**Tips for troubleshooting**
1. Ensure the file type is set to the correct option in the dropdown. Sometimes the ML service will detect a file to be a different type than what is uploaded
2. Check to be sure the transform type is set to a supported transform type
3. If transform attempts continue to fail manually check the uploaded file to ensure it is formatted correctly, then attempt to reupload the file

<img src="${quickFormErrorDetailsModalUrl}" alt="Error Details Modal" style="max-width: 1000px; height: auto; display: block; margin: 12px auto;" />

- Once attempted corrections have been made, click the Retry Transformation button to attempt a new transformation 
`;
